import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Card,
  Button,
  Upload,
  message,
  Table,
  Select,
  Input,
  InputNumber,
  Space,
  Typography,
  Alert,
  Row,
  Col,
  Divider,
  Checkbox,
  Switch,
} from 'antd';
import { UploadOutlined, DeleteOutlined } from '@ant-design/icons';
import { api } from '../services/api';
import { fetchProductBrands, type ProductBrandDto } from '../services/productBrandService';
import { fetchBrands, fetchModels, type BrandDto, type ModelDto } from '../services/vehicleService';

const { Title, Text } = Typography;

interface Category {
  id: number;
  categoryName: string;
  parentId?: number | null;
}

interface Unit {
  id?: number;
  Id?: number;
  unitName?: string;
  UnitName?: string;
  unitCode?: string;
  UnitCode?: string;
  name?: string;
  Name?: string;
  symbol?: string;
  Symbol?: string;
  unitSetId?: number;
  UnitSetId?: number;
  isBaseUnit?: boolean;
  IsBaseUnit?: boolean;
  baseRatio?: number;
  BaseRatio?: number;
  conversionFactor?: number;
  ConversionFactor?: number;
}

interface UnitSet {
  id?: number;
  Id?: number;
  name?: string;
  Name?: string;
}

interface XlsxParseResult {
  headers: string[];
  rows: (object | null)[][];
}

interface ColumnMapping {
  mapping: Record<number, string>;
  barcodeColumnIndex?: number | null;
  barcodeType: string;
  /** Görsel var seçiliyse Excel sütunlarıyla eşlenecek; 3 alan için aynı sütun seçilebilir */
  hasImage?: boolean;
  imageOriginalFileNameColumnIndex?: number | null;
  imageOriginalFilePathColumnIndex?: number | null;
  imageThumbnailFilePathColumnIndex?: number | null;
}

interface ImportDefaults {
  categoryId?: number | null;
  productBrandId?: number | null;
  baseUnitId?: number | null;
  unitSetId?: number | null;
  taxRate?: number | null;
  trackStock?: boolean | null;
  initialStock?: number | null;
  description?: string | null;
  gtipCode?: string | null;
  /** Uyumluluk ekle (products/edit ... tab=compatibility ile aynı seçenekler) */
  addCompatibility?: boolean;
  compatibilityBrandId?: number | null;
  compatibilityModelId?: number | null;
  compatibilityYearFrom?: number | null;
  compatibilityYearTo?: number | null;
  compatibilityIsPrimary?: boolean;
  compatibilityNotes?: string | null;
  compatibilityIsVerified?: boolean;
}

interface LastImport {
  id: number;
  name: string;
  createdAt: string;
  addedCount: number;
  allowRollback: boolean;
  isRolledBack: boolean;
}

// Products tablosu alanları - Türkçe etiketlerle
const PRODUCT_FIELD_OPTIONS = [
  { value: '', label: '(Eşleme yok)' },
  { value: 'productCode', label: 'Ürün Kodu' },
  { value: 'productName', label: 'Ürün Adı' },
  { value: 'barcode', label: 'Barkod' },
  { value: 'categoryId', label: 'Kategori' },
  { value: 'productBrandId', label: 'Marka' },
  { value: 'baseUnitId', label: 'Birim' },
  { value: 'unitSetId', label: 'Birim Seti' },
  { value: 'taxRate', label: 'KDV (%)' },
  { value: 'trackStock', label: 'Stok Takip' },
  { value: 'description', label: 'Açıklama' },
  { value: 'gtipCode', label: 'GTIP Kodu' },
  { value: 'initialStock', label: 'Başlangıç Stok' },
  { value: 'isPriceVariant', label: 'Fiyat Varyantı' },
  { value: 'isWholesalePrice', label: 'Toptan Fiyat' },
  { value: 'isPurchasePrice', label: 'Alış Fiyatı' },
  { value: 'useAutoMargin', label: 'Otomatik Kar' },
  { value: 'retailMarginPercent', label: 'Perakende Kar %' },
  { value: 'wholesaleMarginPercent', label: 'Toptan Kar %' },
];

const BARCODE_TYPES = [
  { value: 'EAN-13', label: 'EAN-13' },
  { value: 'EAN-8', label: 'EAN-8' },
  { value: 'UPC', label: 'UPC' },
  { value: 'Code128', label: 'Code 128' },
  { value: 'Code39', label: 'Code 39' },
  { value: 'QR', label: 'QR' },
];

const ImportStock: React.FC = () => {
  const [file, setFile] = useState<File | null>(null);
  const [parseResult, setParseResult] = useState<XlsxParseResult | null>(null);
  const [importName, setImportName] = useState('');
  const [columnMapping, setColumnMapping] = useState<ColumnMapping>({
    mapping: {},
    barcodeType: 'EAN-13',
    hasImage: false,
  });
  const [defaults, setDefaults] = useState<ImportDefaults>({
    taxRate: 20,
    trackStock: true,
    initialStock: 0,
  });
  const [required, setRequired] = useState<{
    categoryId: number | null;
    baseUnitId: number | null;
    unitSetId: number | null;
  }>({ categoryId: null, baseUnitId: null, unitSetId: null });
  const requiredRef = useRef(required);
  requiredRef.current = required;
  const [lastImport, setLastImport] = useState<LastImport | null>(null);
  const [loading, setLoading] = useState(false);
  const [parseLoading, setParseLoading] = useState(false);
  const [categories, setCategories] = useState<Category[]>([]);
  const [units, setUnits] = useState<Unit[]>([]);
  const [unitSets, setUnitSets] = useState<UnitSet[]>([]);
  const [productBrands, setProductBrands] = useState<ProductBrandDto[]>([]);
  const [compatibilityBrands, setCompatibilityBrands] = useState<BrandDto[]>([]);
  const [compatibilityModels, setCompatibilityModels] = useState<ModelDto[]>([]);

  const loadLastImport = useCallback(async () => {
    try {
      const { data } = await api.get<LastImport | null>('/productimport/last-import');
      setLastImport(data ?? null);
    } catch {
      setLastImport(null);
    }
  }, []);

  const loadCategories = async () => {
    try {
      const { data } = await api.get<Category[]>('/categories');
      const list = Array.isArray(data) ? data : [];
      setCategories(list);
      const validCategories = list.filter(c => (c as { id?: number; Id?: number }).id != null || (c as { id?: number; Id?: number }).Id != null);
      if (validCategories.length > 0) {
        const firstId = (validCategories[0] as { id?: number; Id?: number }).id ?? (validCategories[0] as { id?: number; Id?: number }).Id;
        setRequired(prev => (prev.categoryId == null ? { ...prev, categoryId: Number(firstId) } : prev));
      }
    } catch {
      setCategories([]);
    }
  };

  // Kategori yolunu oluştur (Ana > Alt > Seçilen) - /products/add ile aynı
  const getCategoryPath = (categoryId: number): string => {
    const path: string[] = [];
    let currentId: number | null | undefined = categoryId;
    const cats = categories.filter(c => (c as { id?: number }).id != null || (c as { Id?: number }).Id != null);
    while (currentId) {
      const category = cats.find(c => ((c as { id?: number }).id ?? (c as { Id?: number }).Id) === currentId);
      if (!category) break;
      const name = (category as { categoryName?: string }).categoryName ?? (category as { CategoryName?: string }).CategoryName ?? '';
      path.unshift(name);
      currentId = (category as { parentId?: number | null }).parentId ?? undefined;
    }
    return path.join(' > ');
  };

  // Kategorileri hiyerarşik sırala - /products/add ile aynı
  const sortCategoriesHierarchically = (cats: Category[]): Array<Category & { level?: number }> => {
    const result: Array<Category & { level?: number }> = [];
    const getId = (c: Category) => (c as { id?: number }).id ?? (c as { Id?: number }).Id!;
    const getParentId = (c: Category) => (c as { parentId?: number | null }).parentId ?? null;
    const addCategoryAndChildren = (categoryId: number, level: number = 0) => {
      const category = cats.find(c => getId(c) === categoryId);
      if (!category) return;
      result.push({ ...category, level });
      const children = cats.filter(c => getParentId(c) === categoryId);
      children.forEach(child => addCategoryAndChildren(getId(child), level + 1));
    };
    const roots = cats.filter(c => getParentId(c) == null);
    roots.forEach(root => addCategoryAndChildren(getId(root)));
    return result;
  };

  const loadUnits = async () => {
    try {
      const resUnits = await api.get('/units');
      const resSets = await api.get('/unitsets');
      const unitData = resUnits?.data;
      const setData = resSets?.data;
      const unitList = Array.isArray(unitData) ? unitData : (unitData && typeof unitData === 'object' && !Array.isArray(unitData) ? [] : []);
      const setList = Array.isArray(setData) ? setData : (setData && typeof setData === 'object' && !Array.isArray(setData) ? [] : []);
      setUnits(unitList);
      setUnitSets(setList);
      // AddProduct ile aynı mantık: temel birimden varsayılan birim ve birim seti ata
      if (unitList.length > 0) {
        const getUnitId = (u: Unit) => u.id ?? (u as { Id?: number }).Id;
        const getUnitSetId = (u: Unit) => (u as any).unitSetId ?? (u as any).UnitSetId ?? 1;
        const baseUnit = unitList.find((u: Unit) =>
          (u as any).isBaseUnit === true || (u as any).IsBaseUnit === true ||
          (u as any).baseRatio === 1 || (u as any).BaseRatio === 1 ||
          (u as any).conversionFactor === 1 || (u as any).ConversionFactor === 1
        );
        const firstUnit = unitList[0];
        const defaultUnitId = baseUnit ? getUnitId(baseUnit) : getUnitId(firstUnit);
        const defaultUnitSetId = baseUnit ? getUnitSetId(baseUnit) : getUnitSetId(firstUnit);
        if (defaultUnitId != null && defaultUnitSetId != null) {
          setRequired(prev => ({ ...prev, baseUnitId: Number(defaultUnitId), unitSetId: Number(defaultUnitSetId) }));
        }
      }
    } catch (e) {
      console.error('ImportStock loadUnits/loadUnitSets error:', e);
      setUnits([]);
      setUnitSets([]);
    }
  };

  const loadProductBrands = async () => {
    try {
      const list = await fetchProductBrands();
      setProductBrands(Array.isArray(list) ? list : []);
    } catch {
      setProductBrands([]);
    }
  };

  const loadCompatibilityBrands = async () => {
    try {
      const list = await fetchBrands();
      setCompatibilityBrands(Array.isArray(list) ? list : []);
    } catch {
      setCompatibilityBrands([]);
    }
  };

  useEffect(() => {
    loadLastImport();
    loadCategories();
    loadUnits();
    loadProductBrands();
    loadCompatibilityBrands();
  }, [loadLastImport]);

  useEffect(() => {
    if (defaults.compatibilityBrandId != null && defaults.compatibilityBrandId > 0) {
      fetchModels(defaults.compatibilityBrandId).then(list => setCompatibilityModels(Array.isArray(list) ? list : [])).catch(() => setCompatibilityModels([]));
    } else {
      setCompatibilityModels([]);
    }
  }, [defaults.compatibilityBrandId]);

  const handleFileUpload = async (f: File) => {
    if (!f.name.toLowerCase().endsWith('.xlsx')) {
      message.error('Sadece .xlsx dosyası yükleyin.');
      return;
    }
    setParseLoading(true);
    setFile(f);
    setParseResult(null);
    try {
      const form = new FormData();
      form.append('file', f);
      const { data } = await api.post<XlsxParseResult>('/productimport/parse-xlsx', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setParseResult({
        headers: data.headers ?? [],
        rows: (data.rows ?? []).slice(0, 200),
      });
      setColumnMapping(prev => ({ ...prev, mapping: {} }));
      message.success('Dosya işlendi. Sütun eşlemesi yapın.');
    } catch (e: any) {
      message.error(e.response?.data?.message || 'Dosya işlenemedi.');
    } finally {
      setParseLoading(false);
    }
  };

  const setMappingForColumn = (colIndex: number, fieldCode: string) => {
    setColumnMapping(prev => {
      const next = { ...prev, mapping: { ...prev.mapping } };
      if (fieldCode) next.mapping[colIndex] = fieldCode;
      else delete next.mapping[colIndex];
      return next;
    });
  };

  const setBarcodeColumn = (colIndex: number | undefined) => {
    setColumnMapping(prev => ({
      ...prev,
      barcodeColumnIndex: colIndex === undefined ? undefined : colIndex,
    }));
  };

  const handleExecuteImport = async () => {
    if (!file || !importName.trim()) {
      message.warning('Import adı girin ve dosya seçin.');
      return;
    }
    const r = requiredRef.current;
    let catId: number | null = r.categoryId != null && r.categoryId > 0 ? r.categoryId : null;
    let unitId: number | null = r.baseUnitId != null && r.baseUnitId > 0 ? r.baseUnitId : null;
    let setId: number | null = r.unitSetId != null && r.unitSetId > 0 ? r.unitSetId : null;
    if (catId == null && categories.length > 0) {
      const c = categories.find(cc => (cc as { id?: number; Id?: number }).id != null || (cc as { id?: number; Id?: number }).Id != null);
      if (c) catId = Number((c as { id?: number; Id?: number }).id ?? (c as { id?: number; Id?: number }).Id);
    }
    if ((unitId == null || setId == null) && units.length > 0) {
      const baseUnit = units.find((u: Unit) =>
        (u as any).isBaseUnit === true || (u as any).IsBaseUnit === true ||
        (u as any).baseRatio === 1 || (u as any).BaseRatio === 1 ||
        (u as any).conversionFactor === 1 || (u as any).ConversionFactor === 1
      );
      const u = baseUnit ?? units[0];
      if (unitId == null) unitId = (u as Unit).id ?? (u as { Id?: number }).Id ?? null;
      if (setId == null) setId = (u as any).unitSetId ?? (u as any).UnitSetId ?? 1;
    }
    if (setId == null && unitSets.length > 0) {
      const first = unitSets.find(us => (us as { id?: number; Id?: number }).id != null || (us as { id?: number; Id?: number }).Id != null);
      if (first) setId = Number((first as { id?: number; Id?: number }).id ?? (first as { id?: number; Id?: number }).Id);
    }
    if (unitId == null || unitId <= 0 || setId == null || setId <= 0) {
      message.warning('Lütfen varsayılan değerlerden Birim ve Birim Seti seçin.');
      return;
    }
    requiredRef.current = { ...r, categoryId: catId ?? null, baseUnitId: unitId, unitSetId: setId };
    setRequired(prev => ({ ...prev, categoryId: catId ?? null, baseUnitId: unitId, unitSetId: setId }));
    const d = {
      ...defaults,
      categoryId: catId ?? null,
      baseUnitId: unitId,
      unitSetId: setId,
    };
    setLoading(true);
    try {
      const form = new FormData();
      form.append('file', file);
      form.append('importName', importName.trim());
      form.append(
        'columnMappingJson',
        JSON.stringify({
          mapping: columnMapping.mapping,
          barcodeColumnIndex: columnMapping.barcodeColumnIndex,
          barcodeType: columnMapping.barcodeType,
          hasImage: columnMapping.hasImage,
          imageOriginalFileNameColumnIndex: columnMapping.imageOriginalFileNameColumnIndex,
          imageOriginalFilePathColumnIndex: columnMapping.imageOriginalFilePathColumnIndex,
          imageThumbnailFilePathColumnIndex: columnMapping.imageThumbnailFilePathColumnIndex,
        })
      );
      const defaultsPayload = {
        ...d,
        categoryId: catId != null ? Number(catId) : null,
        baseUnitId: unitId != null ? Number(unitId) : null,
        unitSetId: setId != null ? Number(setId) : null,
        productBrandId: defaults.productBrandId != null && defaults.productBrandId > 0 ? Number(defaults.productBrandId) : null,
        addCompatibility: defaults.addCompatibility ?? false,
        compatibilityBrandId: defaults.addCompatibility && defaults.compatibilityBrandId != null && defaults.compatibilityBrandId > 0 ? Number(defaults.compatibilityBrandId) : null,
        compatibilityModelId: defaults.compatibilityModelId != null && defaults.compatibilityModelId > 0 ? Number(defaults.compatibilityModelId) : null,
        compatibilityYearFrom: defaults.compatibilityYearFrom != null ? Number(defaults.compatibilityYearFrom) : null,
        compatibilityYearTo: defaults.compatibilityYearTo != null ? Number(defaults.compatibilityYearTo) : null,
        compatibilityIsPrimary: defaults.compatibilityIsPrimary ?? false,
        compatibilityNotes: defaults.compatibilityNotes ?? null,
        compatibilityIsVerified: defaults.compatibilityIsVerified ?? false,
        // Backend DTO ile kesin eşleşme için PascalCase kopyaları da gönder
        CategoryId: catId != null ? Number(catId) : null,
        BaseUnitId: unitId != null ? Number(unitId) : null,
        UnitSetId: setId != null ? Number(setId) : null,
        ProductBrandId: defaults.productBrandId != null && defaults.productBrandId > 0 ? Number(defaults.productBrandId) : null,
        AddCompatibility: defaults.addCompatibility ?? false,
        CompatibilityBrandId: defaults.addCompatibility && defaults.compatibilityBrandId != null && defaults.compatibilityBrandId > 0 ? Number(defaults.compatibilityBrandId) : null,
        CompatibilityModelId: defaults.compatibilityModelId != null && defaults.compatibilityModelId > 0 ? Number(defaults.compatibilityModelId) : null,
        CompatibilityYearFrom: defaults.compatibilityYearFrom != null ? Number(defaults.compatibilityYearFrom) : null,
        CompatibilityYearTo: defaults.compatibilityYearTo != null ? Number(defaults.compatibilityYearTo) : null,
        CompatibilityIsPrimary: defaults.compatibilityIsPrimary ?? false,
        CompatibilityNotes: defaults.compatibilityNotes ?? null,
        CompatibilityIsVerified: defaults.compatibilityIsVerified ?? false,
      };
      form.append('defaultsJson', JSON.stringify(defaultsPayload));
      // [GEÇİCİ LOG] Gönderilen varsayılanlar ve eşleme
      console.log('[Import] Gönderilen defaults:', defaultsPayload);
      console.log('[Import] Gönderilen columnMapping:', {
        mapping: columnMapping.mapping,
        barcodeColumnIndex: columnMapping.barcodeColumnIndex,
        barcodeType: columnMapping.barcodeType,
        hasImage: columnMapping.hasImage,
        imageOriginalFileNameColumnIndex: columnMapping.imageOriginalFileNameColumnIndex,
        imageOriginalFilePathColumnIndex: columnMapping.imageOriginalFilePathColumnIndex,
        imageThumbnailFilePathColumnIndex: columnMapping.imageThumbnailFilePathColumnIndex,
      });
      const { data: result } = await api.post<{
        importId: number;
        importName: string;
        addedCount: number;
        totalRows: number;
        skippedCount?: number;
        firstError?: string | null;
      }>('/productimport/execute-import', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      loadLastImport();
      setFile(null);
      setParseResult(null);
      setImportName('');
      if (result.addedCount === 0) {
        const err = (result as { firstError?: string | null; FirstError?: string | null }).firstError
          ?? (result as { firstError?: string | null; FirstError?: string | null }).FirstError;
        const detail = err
          ? ` Sebep: ${err}`
          : result.totalRows === 0
            ? ' Excel\'den 0 satır okundu; dosyada veri satırı (başlık hariç) var mı kontrol edin.'
            : ` ${result.totalRows} satır işlendi, ${result.skippedCount ?? result.totalRows} atlandı. Sütun eşlemesinde Ürün Kodu/Ürün Adı ve Birim/Birim Seti varsayılanları kontrol edin.`;
        message.warning(`Hiç ürün eklenmedi.${detail}`);
      } else {
        const skippedInfo = (result.skippedCount ?? 0) > 0 ? ` (${result.skippedCount} satır atlandı)` : '';
        message.success(`İçe aktarma tamamlandı: ${result.addedCount} ürün eklendi${skippedInfo}.`);
      }
    } catch (e: any) {
      const data = e.response?.data;
      const msg =
        (typeof data === 'string' && data) ||
        data?.message ||
        data?.detail ||
        data?.title ||
        e.message ||
        'İçe aktarma başarısız.';
      console.error('[Import] execute-import error:', e.response?.status, data, e);
      message.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleRollback = async () => {
    if (!lastImport?.id || lastImport.isRolledBack) return;
    if (!lastImport.allowRollback) {
      message.warning(
        'Geri almak için veritabanında ImportXLSX tablosunda bu kaydın AllowRollback alanını true yapmanız gerekir.'
      );
      return;
    }
    setLoading(true);
    try {
      await api.post(`/productimport/rollback/${lastImport.id}`);
      message.success('Geri alma işlemi tamamlandı.');
      loadLastImport();
    } catch (e: any) {
      message.error(e.response?.data?.message || 'Geri alma başarısız.');
    } finally {
      setLoading(false);
    }
  };

  const previewRows = parseResult?.rows?.slice(0, 10) ?? [];
  const headers = parseResult?.headers ?? [];

  return (
    <div style={{ padding: '0 24px' }}>
      <Title level={4}>Stok İçe Aktar (XLSX)</Title>

      {/* Son yükleme bilgisi */}
      {lastImport && (
        <Card size="small" style={{ marginBottom: 16 }}>
          <Row align="middle" justify="space-between">
            <Col>
              <Text strong>Son yükleme: </Text>
              <Text>{lastImport.name}</Text>
              <Text type="secondary" style={{ marginLeft: 8 }}>
                {new Date(lastImport.createdAt).toLocaleString('tr-TR')} · {lastImport.addedCount} kayıt
              </Text>
              {lastImport.isRolledBack && (
                <Text type="danger" style={{ marginLeft: 8 }}>
                  (Geri alındı)
                </Text>
              )}
            </Col>
            <Col>
              {!lastImport.isRolledBack && (
                <Button
                  type="link"
                  danger
                  icon={<DeleteOutlined />}
                  onClick={handleRollback}
                  disabled={!lastImport.allowRollback || loading}
                >
                  Geri al
                </Button>
              )}
            </Col>
          </Row>
          {!lastImport.allowRollback && !lastImport.isRolledBack && (
            <Alert
              type="info"
              message="Geri almak için veritabanında import_x_l_s_x tablosunda bu kaydın allow_rollback alanını true yapın."
              style={{ marginTop: 8 }}
              showIcon
            />
          )}
        </Card>
      )}

      <Card title="1. XLSX Dosyası Seç" style={{ marginBottom: 16 }}>
        <Upload
          accept=".xlsx"
          maxCount={1}
          beforeUpload={(f) => {
            handleFileUpload(f);
            return false;
          }}
          fileList={file ? [{ uid: '1', name: file.name, status: 'done' }] : []}
          onRemove={() => {
            setFile(null);
            setParseResult(null);
          }}
        >
          <Button icon={<UploadOutlined />} loading={parseLoading}>
            XLSX Yükle
          </Button>
        </Upload>
      </Card>

      {parseResult && (
        <>
          <Card title="2. Sütun Eşlemesi ve Varsayılan Değerler" style={{ marginBottom: 16 }}>
            <Row gutter={24}>
              <Col span={12}>
                <Divider orientation="left">Varsayılan değerler (Excel’de olmayan alanlar)</Divider>
                <Space direction="vertical" style={{ width: '100%' }} size="small">
                  <div>
                    <Text type="secondary">Kategori </Text>
                    <Space.Compact style={{ width: '100%', marginTop: 4 }}>
                      <Select
                        placeholder="Kategori seçin"
                        allowClear
                        showSearch
                        filterOption={(input, option) => {
                          const searchText = `${option?.label ?? ''}`.toLowerCase();
                          return searchText.includes(input.toLowerCase());
                        }}
                        optionLabelProp="label"
                        style={{ width: '85%' }}
                        value={required.categoryId ?? undefined}
                        onChange={v => {
                          const next = { ...requiredRef.current, categoryId: v != null ? Number(v) : null };
                          requiredRef.current = next;
                          setRequired(next);
                        }}
                      >
                        {sortCategoriesHierarchically(
                          categories.filter(c => (c as { id?: number; Id?: number }).id != null || (c as { id?: number; Id?: number }).Id != null)
                        ).map((cat: Category & { level?: number }) => {
                          const id = (cat as { id?: number }).id ?? (cat as { Id?: number }).Id!;
                          const level = cat.level ?? 0;
                          const indent = '　'.repeat(level);
                          const prefix = level > 0 ? '├─ ' : '📁 ';
                          const fullPath = getCategoryPath(id);
                          const hasChildren = categories.some(c => ((c as { parentId?: number | null }).parentId ?? null) === id);
                          const name = (cat as { categoryName?: string }).categoryName ?? (cat as { CategoryName?: string }).CategoryName ?? '-';
                          return (
                            <Select.Option key={id} value={id} label={fullPath} disabled={hasChildren}>
                              <span style={{ fontFamily: 'monospace' }}>
                                {indent}{prefix}{name}
                              </span>
                            </Select.Option>
                          );
                        })}
                      </Select>
                      <Button
                        type="dashed"
                        onClick={() => window.open('/categories', '_blank')}
                        style={{ width: '15%' }}
                        title="Yeni kategori ekle"
                      >
                        ➕
                      </Button>
                    </Space.Compact>
                  </div>
                  <div>
                    <Text type="secondary">Birim </Text>
                    <Select
                      placeholder="Birim seçin"
                      allowClear
                      style={{ width: '100%', marginTop: 4 }}
                      value={required.baseUnitId ?? undefined}
                      onChange={v => {
                        const next = { ...requiredRef.current, baseUnitId: v != null ? Number(v) : null };
                        requiredRef.current = next;
                        setRequired(next);
                      }}
                      options={units
                        .filter(u => (u.id ?? (u as { Id?: number }).Id) != null)
                        .map(u => {
                          const id = u.id ?? (u as { Id?: number }).Id;
                          const label = (u as any).UnitName ?? u.unitName ?? (u as any).UnitCode ?? u.unitCode ?? (u as any).Name ?? u.name ?? (u as any).Symbol ?? u.symbol ?? `Birim ${id}`;
                          return { value: Number(id), label: String(label || id) };
                        })}
                    />
                  </div>
                  <div>
                    <Text type="secondary">Birim Seti </Text>
                    <Select
                      placeholder="Birim seti seçin"
                      allowClear
                      style={{ width: '100%', marginTop: 4 }}
                      value={required.unitSetId ?? undefined}
                      onChange={v => {
                        const next = { ...requiredRef.current, unitSetId: v != null ? Number(v) : null };
                        requiredRef.current = next;
                        setRequired(next);
                      }}
                      options={unitSets
                        .filter(u => (u.id ?? (u as { Id?: number }).Id) != null)
                        .map(u => {
                          const id = u.id ?? (u as { Id?: number }).Id;
                          const label = (u as any).Name ?? u.name ?? `Birim seti ${id}`;
                          return { value: Number(id), label: String(label || id) };
                        })}
                    />
                  </div>
                  <div>
                    <Text type="secondary">Ürün markası </Text>
                    <Select
                      placeholder="Marka seçin (opsiyonel)"
                      allowClear
                      showSearch
                      optionFilterProp="children"
                      style={{ width: '100%', marginTop: 4 }}
                      value={defaults.productBrandId ?? undefined}
                      onChange={v => setDefaults(d => ({ ...d, productBrandId: v != null ? Number(v) : null }))}
                      options={productBrands.map(b => ({ value: b.id, label: b.name }))}
                    />
                  </div>
                  <div>
                    <Text type="secondary">KDV (%) </Text>
                    <InputNumber
                      min={0}
                      max={100}
                      style={{ width: '100%', marginTop: 4 }}
                      value={defaults.taxRate ?? 20}
                      onChange={v => setDefaults(d => ({ ...d, taxRate: v ?? 20 }))}
                    />
                  </div>
                  <div>
                    <Text type="secondary">Başlangıç Stok </Text>
                    <InputNumber
                      min={0}
                      style={{ width: '100%', marginTop: 4 }}
                      value={defaults.initialStock ?? 0}
                      onChange={v => setDefaults(d => ({ ...d, initialStock: v ?? 0 }))}
                    />
                  </div>
                </Space>
              </Col>
              <Col span={12}>
                <Divider orientation="left">Görsel</Divider>
                <div style={{ marginBottom: 16 }}>
                  <Checkbox
                    checked={columnMapping.hasImage ?? false}
                    onChange={e => setColumnMapping(c => ({ ...c, hasImage: e.target.checked }))}
                  >
                    Görsel var
                  </Checkbox>
                  {(columnMapping.hasImage ?? false) && (
                    <Space direction="vertical" style={{ width: '100%', marginTop: 8 }} size="small">
                      <div>
                        <Text type="secondary">original_file_name sütunu </Text>
                        <Select
                          placeholder="Sütun seçin (3 alan için aynı sütun seçilebilir)"
                          allowClear
                          style={{ width: '100%', marginTop: 4 }}
                          value={columnMapping.imageOriginalFileNameColumnIndex ?? undefined}
                          onChange={v => setColumnMapping(c => ({ ...c, imageOriginalFileNameColumnIndex: v ?? null }))}
                          options={headers.map((h, i) => ({ value: i, label: `${i + 1}. ${h || '(Boş)'}` }))}
                        />
                      </div>
                      <div>
                        <Text type="secondary">original_file_path sütunu </Text>
                        <Select
                          placeholder="Sütun seçin"
                          allowClear
                          style={{ width: '100%', marginTop: 4 }}
                          value={columnMapping.imageOriginalFilePathColumnIndex ?? undefined}
                          onChange={v => setColumnMapping(c => ({ ...c, imageOriginalFilePathColumnIndex: v ?? null }))}
                          options={headers.map((h, i) => ({ value: i, label: `${i + 1}. ${h || '(Boş)'}` }))}
                        />
                      </div>
                      <div>
                        <Text type="secondary">thumbnail_file_path sütunu </Text>
                        <Select
                          placeholder="Sütun seçin"
                          allowClear
                          style={{ width: '100%', marginTop: 4 }}
                          value={columnMapping.imageThumbnailFilePathColumnIndex ?? undefined}
                          onChange={v => setColumnMapping(c => ({ ...c, imageThumbnailFilePathColumnIndex: v ?? null }))}
                          options={headers.map((h, i) => ({ value: i, label: `${i + 1}. ${h || '(Boş)'}` }))}
                        />
                      </div>
                    </Space>
                  )}
                </div>
                <Divider orientation="left">Barkod</Divider>
                <div style={{ marginBottom: 8 }}>
                  <Text type="secondary">Barkod sütunu </Text>
                  <Select
                    placeholder="Sütun seçin (opsiyonel)"
                    allowClear
                    style={{ width: '100%', marginTop: 4 }}
                    value={columnMapping.barcodeColumnIndex ?? undefined}
                    onChange={v => setBarcodeColumn(v)}
                    options={headers.map((h, i) => ({ value: i, label: `${i + 1}. ${h || '(Boş)'}` }))}
                  />
                </div>
                <div>
                  <Text type="secondary">Barkod türü </Text>
                  <Select
                    style={{ width: '100%', marginTop: 4 }}
                    value={columnMapping.barcodeType}
                    onChange={v => setColumnMapping(c => ({ ...c, barcodeType: v }))}
                    options={BARCODE_TYPES}
                  />
                </div>
                <Divider orientation="left">Uyumluluk</Divider>
                <div style={{ marginBottom: 16 }}>
                  <Checkbox
                    checked={defaults.addCompatibility ?? false}
                    onChange={e => setDefaults(d => ({ ...d, addCompatibility: e.target.checked }))}
                  >
                    Uyumluluk ekle
                  </Checkbox>
                  {(defaults.addCompatibility ?? false) && (
                    <Space direction="vertical" style={{ width: '100%', marginTop: 8 }} size="small">
                      <div>
                        <Text type="secondary">Marka (araç) </Text>
                        <Select
                          placeholder="Marka seçin"
                          allowClear
                          showSearch
                          filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
                          style={{ width: '100%', marginTop: 4 }}
                          value={defaults.compatibilityBrandId ?? undefined}
                          onChange={v => {
                            setDefaults(d => ({ ...d, compatibilityBrandId: v != null ? Number(v) : null, compatibilityModelId: null }));
                          }}
                          options={compatibilityBrands.map(b => ({ value: b.id, label: b.name }))}
                        />
                      </div>
                      <div>
                        <Text type="secondary">Model (opsiyonel) </Text>
                        <Select
                          placeholder={defaults.compatibilityBrandId ? 'Model seçin (opsiyonel)' : 'Önce marka seçin'}
                          allowClear
                          showSearch
                          filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
                          style={{ width: '100%', marginTop: 4 }}
                          value={defaults.compatibilityModelId ?? undefined}
                          onChange={v => setDefaults(d => ({ ...d, compatibilityModelId: v != null ? Number(v) : null }))}
                          options={compatibilityModels.map(m => ({ value: m.id, label: m.name }))}
                          disabled={!defaults.compatibilityBrandId}
                        />
                      </div>
                      <div>
                        <Text type="secondary">Başlangıç Yılı (opsiyonel) </Text>
                        <InputNumber
                          placeholder="Örn: 2010"
                          min={1900}
                          max={2100}
                          style={{ width: '100%', marginTop: 4 }}
                          value={defaults.compatibilityYearFrom ?? undefined}
                          onChange={v => setDefaults(d => ({ ...d, compatibilityYearFrom: v ?? null }))}
                        />
                      </div>
                      <div>
                        <Text type="secondary">Bitiş Yılı (opsiyonel) </Text>
                        <InputNumber
                          placeholder="Örn: 2014"
                          min={1900}
                          max={2100}
                          style={{ width: '100%', marginTop: 4 }}
                          value={defaults.compatibilityYearTo ?? undefined}
                          onChange={v => setDefaults(d => ({ ...d, compatibilityYearTo: v ?? null }))}
                        />
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <Text type="secondary">Ana uyumluluk </Text>
                        <Switch
                          checked={defaults.compatibilityIsPrimary ?? false}
                          onChange={v => setDefaults(d => ({ ...d, compatibilityIsPrimary: v }))}
                        />
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <Text type="secondary">Onaylı </Text>
                        <Switch
                          checked={defaults.compatibilityIsVerified ?? false}
                          onChange={v => setDefaults(d => ({ ...d, compatibilityIsVerified: v }))}
                        />
                      </div>
                      <div>
                        <Text type="secondary">Notlar </Text>
                        <Input.TextArea
                          rows={2}
                          placeholder="Ek notlar..."
                          style={{ marginTop: 4 }}
                          value={defaults.compatibilityNotes ?? ''}
                          onChange={e => setDefaults(d => ({ ...d, compatibilityNotes: e.target.value || null }))}
                        />
                      </div>
                    </Space>
                  )}
                </div>
              </Col>
            </Row>
          </Card>

          <Card title="3. Sütunları Ürün Alanlarına Eşleyin" style={{ marginBottom: 16 }}>
            <Table
              size="small"
              pagination={false}
              scroll={{ x: 'max-content' }}
              dataSource={previewRows.map((row, i) => ({
                key: i,
                ...(Array.isArray(row)
                  ? (row as object[]).reduce((acc, val, j) => ({ ...acc, [`col_${j}`]: val }), {} as Record<string, unknown>)
                  : {}),
              }))}
              columns={[
                {
                  title: '#',
                  width: 40,
                  render: (_, __, i) => i + 1,
                },
                ...headers.map((h, colIndex) => ({
                  title: (
                    <div key={colIndex}>
                      <div style={{ marginBottom: 4 }}>
                        <Select
                          placeholder="Alan"
                          style={{ width: '100%', minWidth: 140 }}
                          value={columnMapping.mapping[colIndex] ?? ''}
                          onChange={v => setMappingForColumn(colIndex, v)}
                          options={PRODUCT_FIELD_OPTIONS}
                        />
                      </div>
                      <Text type="secondary" style={{ fontSize: 11 }}>
                        {h || `Sütun ${colIndex + 1}`}
                      </Text>
                    </div>
                  ),
                  dataIndex: `col_${colIndex}`,
                  key: `col_${colIndex}`,
                  width: 160,
                  ellipsis: true,
                  render: (val: unknown) =>
                    val != null ? String(val).slice(0, 30) + (String(val).length > 30 ? '…' : '') : '',
                })),
              ]}
            />
          </Card>

          <Card title="4. İçe Aktar">
            <Space>
              <Input
                placeholder="Import adı (örn: Mart 2025 stok)"
                value={importName}
                onChange={e => setImportName(e.target.value)}
                style={{ width: 280 }}
              />
              <Button type="primary" loading={loading} onClick={handleExecuteImport}>
                İçe Aktar
              </Button>
            </Space>
          </Card>
        </>
      )}
    </div>
  );
};

export default ImportStock;
